import type { QueryClient } from "@tanstack/react-query";
import { invalidateNotifications } from "web_myplann/Api/Notifications/notificatioKeys";
import { openQuestionnaireFromPush } from "web_myplann/Api/Nsure/openQuestionnaire";
import {
	patchNotificationsIdReadV2,
	patchNotificationsReadV2,
} from "web_myplann/Infrastructure/Api/Api";
import { handleCall } from "web_myplann/Infrastructure/Api/ApiUtils";
import { logWarning } from "web_myplann/Infrastructure/sentry";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { buildReminderEntityRoute } from "web_myplann/NotificationCenter/Utils/buildReminderEntityRoute";
import { parseReminderPushTarget } from "web_myplann/NotificationCenter/Utils/parseReminderPushTarget";
import {
	fallbackToNotificationList,
	getDestinationRoute,
	isInvestmentQuestionnaireDestination,
	isNoDeepLinkGroup,
	isReminderDestination,
	isWebDestination,
	type NavigateFn,
	navigateToSource,
} from "web_myplann/Utils/WebViewHandlers/NotificationDestinations";
import {
	getMessageId,
	isDuplicate,
	sendReceipt,
} from "web_myplann/Utils/WebViewHandlers/NotificationReceipt";
import {
	type NavigateNotificationRequest,
	parseNotificationId,
} from "web_myplann/Utils/WebViewModel";

export const collectPushNotificationIds = (payload: {
	id?: number;
	ids?: number[];
}): number[] => {
	const candidates: unknown[] = [
		...(Array.isArray(payload.ids) ? payload.ids : []),
		payload.id,
	];
	const unique = new Set<number>();
	for (const candidate of candidates) {
		const parsed = parseNotificationId(candidate);
		if (parsed !== undefined) {
			unique.add(parsed);
		}
	}
	return [...unique];
};

const markNotificationsRead = (ids: number[], queryClient: QueryClient) => {
	if (ids.length === 0) {
		return;
	}
	const request =
		ids.length === 1
			? patchNotificationsIdReadV2(ids[0])
			: patchNotificationsReadV2({ notificationIds: ids });
	// Failures are already reported to Sentry by handleCall; no toast, no retry.
	void handleCall(request, { skipToastFor: true }).then((result) => {
		if (result.isSuccess) {
			invalidateNotifications(queryClient);
		}
	});
};

export const handleNotificationNavigation = async (
	message: NavigateNotificationRequest,
	queryClient: QueryClient,
	navigate: NavigateFn,
	getCurrentPath: () => string,
) => {
	const messageId = getMessageId(message);
	if (messageId) {
		sendReceipt(messageId);
		if (isDuplicate(messageId)) {
			return;
		}
	}

	const { payload } = message;
	const notificationId = parseNotificationId(payload.id);

	markNotificationsRead(collectPushNotificationIds(payload), queryClient);

	if (
		isInvestmentQuestionnaireDestination(payload.type, payload.notificationType)
	) {
		const opened = await openQuestionnaireFromPush(
			{
				notificationId,
				clientType: payload.clientType,
				identificationNumber: payload.identificationNumber,
			},
			queryClient,
		);
		if (!opened) {
			await navigate(AppRouting.MyPlann.Notifications.path());
		}
		return;
	}

	const route = getDestinationRoute(payload.type);
	if (route) {
		await navigateToSource(route, payload, navigate, getCurrentPath);
		return;
	}

	if (isReminderDestination(payload.type, payload.notificationType)) {
		const target = parseReminderPushTarget(payload);
		if (!target && payload.entityType) {
			// A reminder without a linked entity legitimately lands on the list;
			// a linked entity we cannot route to is a contract problem worth seeing.
			logWarning("Reminder notification with unresolvable entity target", {
				notification_type: payload.type,
				notification_source_type: payload.notificationType,
				notification_id: payload.id,
				reminder_entity_type: payload.entityType,
				reminder_entity_id: payload.entityId,
			});
		}
		await navigate(
			target
				? buildReminderEntityRoute(
						target.entityType,
						target.entityId,
						target.reminderId,
					)
				: AppRouting.MyPlann.Notifications.path("upcoming"),
		);
		return;
	}

	if (isWebDestination(payload.type)) {
		await fallbackToNotificationList(
			"Unhandled notification navigation type",
			payload,
			navigate,
		);
		return;
	}

	if (!payload.type || isNoDeepLinkGroup(payload.type)) {
		return;
	}
	logWarning("Notification navigation without a known destination", {
		notification_type: payload.type,
		notification_source_type: payload.notificationType,
		notification_id: payload.id,
	});
};
